import { Controller } from '@hotwired/stimulus'
import debounce from 'debounce'
import { valueMatched } from '../_input_match_utils'
import { broadcastInputChange } from '../_broadcast_change_utils'

type MaskCallback = (value: string | number, options: Record<string, any>) => string | null

type InputEventCustom = InputEvent & { target: HTMLInputElement }

const DEFAULT_MASK_CALLBACK: MaskCallback = () => ''
const FOCUS_CLASSES = {
  wrapper: ['border-viaorange'],
  mask: [
    'px-3',
    'py-1',
    '!bg-100',
    'border',
    'border-300',
    'contrast-more:border-2',
    'text-500',
    'font-bold',
    'shadow-md',
    'translate-y-7'
  ],
  input: ['!text-800']
}

const _toInt = (value: string | number): number =>
  (typeof value === 'string' ? parseInt(value, 10) : value) ?? 0

const _maskCurrencyCallback: (hasZeroExponent: boolean) => MaskCallback =
  hasZeroExponent => (value, options) => {
    const defaultOptions = { currency: 'EUR' }
    const { currency } = { ...defaultOptions, ...options }
    const normalisedValue = (typeof value === 'string' ? parseFloat(value) : value) ?? 0
    return new Intl.NumberFormat('en-UK', {
      style: 'currency',
      currency: currency.toUpperCase(),
      currencyDisplay: 'code',
      minimumFractionDigits: hasZeroExponent ? 0 : 2
    }).format(normalisedValue)
  }

const _maskLength: MaskCallback = (value, options) => {
  const defaultOptions = { unit: 'centimeter' }
  const { unit } = { ...defaultOptions, ...options }
  return new Intl.NumberFormat('en-UK', { style: 'unit', unit, unitDisplay: 'narrow' }).format(
    _toInt(value)
  )
}

const _maskWeight: MaskCallback = (value, options) => {
  const defaultOptions = { unit: 'g' }
  const { unit } = { ...defaultOptions, ...options }
  const rawValue = _toInt(value)
  const absoluteValue = unit === 'kg' ? rawValue * 1000 : rawValue

  const isNegative = absoluteValue < 0
  const normalisedValue = Math.abs(absoluteValue)
  const tons = Math.floor(normalisedValue / 1000000)
  const kgWithGrams = (normalisedValue % 1000000) / 1000
  const kg = Math.floor(kgWithGrams)
  const g = normalisedValue % 1000
  const gString = unit === 'kg' ? '' : ` ${`000${g}`.slice(-3)}g`
  const negativeSign = isNegative ? '-' : ''

  if (tons > 0) {
    return `${negativeSign}${tons}t ${`000${kg}`.slice(-3)}kg${gString}`
  }
  return `${negativeSign}${kg}kg${gString}`
}

const _maskNumberCallback: (suffix?: string) => MaskCallback =
  (suffix = '') =>
  value => {
    return `${parseFloat(String(value).replace(',', '.'))}${suffix}`
  }

/* controller */

export default class extends Controller {
  static targets = ['wrapper', 'mask', 'input', 'hidden']

  declare readonly hasWrapperTarget: boolean
  declare readonly wrapperTarget: HTMLDivElement
  declare readonly hasMaskTarget: boolean
  declare readonly maskTarget: HTMLDivElement
  declare readonly hasInputTarget: boolean
  declare readonly inputTarget: HTMLInputElement
  declare readonly hasHiddenTarget: boolean
  declare readonly hiddenTarget: HTMLInputElement

  declare debouncedBroadcastInputChange: (input: HTMLInputElement) => void

  static values = {
    min: Number,
    max: Number,
    type: String,
    match: String,
    maskOptions: Object,
    forceCase: String,
    currencyHasZeroExponent: Boolean
  }

  declare typeValue: string
  declare readonly hasTypeValue: boolean
  declare matchValue: string
  declare readonly hasMatchValue: boolean
  declare maskOptionsValue: Record<string, any>
  declare readonly hasMaskOptionsValue: boolean
  declare forceCaseValue: string
  declare readonly hasForceCaseValue: boolean
  declare minValue: number
  declare readonly hasMinValue: boolean
  declare maxValue: number
  declare readonly hasMaxValue: boolean
  declare currencyHasZeroExponentValue: boolean

  maskCallback: MaskCallback = DEFAULT_MASK_CALLBACK

  initialize(): void {
    this.debouncedBroadcastInputChange = debounce((input: HTMLInputElement) => {
      broadcastInputChange(input)
    }, 500).bind(this)
  }

  connect(): void {
    // TODO: remember to fix on tsConfig this horrible rule that prevents me from doing maps
    const maskCallbackMapping: any = {
      currency: _maskCurrencyCallback(this.currencyHasZeroExponentValue),
      length: _maskLength,
      weight: _maskWeight,
      percentage: _maskNumberCallback('%'),
      fractional: _maskNumberCallback()
    }
    this.maskCallback =
      this.typeValue in maskCallbackMapping
        ? maskCallbackMapping[this.typeValue]
        : DEFAULT_MASK_CALLBACK
    this.input(null, this.inputTarget)
  }

  focus(): void {
    this.wrapperTarget.classList.add(...FOCUS_CLASSES.wrapper)
    this.maskTarget.classList.add(...FOCUS_CLASSES.mask)
    this.inputTarget.classList.add(...FOCUS_CLASSES.input)
  }

  blur(): void {
    this.wrapperTarget.classList.remove(...FOCUS_CLASSES.wrapper)
    this.maskTarget.classList.remove(...FOCUS_CLASSES.mask)
    this.inputTarget.classList.remove(...FOCUS_CLASSES.input)
  }

  input(e: InputEventCustom | null, inputFromParameter?: HTMLInputElement): void {
    const input = e?.target ?? inputFromParameter
    if (input?.value === '') {
      this.maskTarget.innerText = ''
      if (this.hasHiddenTarget) this.hiddenTarget.value = ''
      return
    }
    if (typeof input === 'undefined') return

    // min and max
    if (this.hasMinValue) {
      input.value = _toInt(input.value) < this.minValue ? String(this.minValue) : input.value
    }
    if (this.hasMaxValue) {
      input.value = _toInt(input.value) > this.maxValue ? String(this.maxValue) : input.value
    }

    // masks
    let maskValue = this.maskCallback(input.value, this.maskOptionsValue)
    if (maskValue?.includes('NaN') === true) maskValue = null
    this.maskTarget.innerText = maskValue ?? ''

    input.value = valueMatched(input.value, this.matchValue ?? null, this.typeValue ?? null)

    // force casing
    if (this.hasForceCaseValue) {
      const cursorPositionIndex = input.selectionStart ?? 0
      if (this.forceCaseValue === 'up') {
        input.value = input.value.toUpperCase()
      } else if (this.forceCaseValue === 'down') {
        input.value = input.value.toLowerCase()
      }
      input.setSelectionRange(cursorPositionIndex, cursorPositionIndex)
    }

    // currency/percentage/fractional - real value hidden input
    if (this.hasHiddenTarget) {
      if (this.typeValue === 'currency') {
        const defaultMultiplier = this.currencyHasZeroExponentValue ? 1 : 100
        const multiplier =
          (this.hasMaskOptionsValue && this.maskOptionsValue.multiplier) ?? defaultMultiplier
        let valueToSend = parseFloat(input.value) * multiplier
        if (multiplier === 100) valueToSend = Math.round(valueToSend)
        this.hiddenTarget.value = String(valueToSend)
      }
      if (this.typeValue === 'percentage' || this.typeValue === 'fractional') {
        this.hiddenTarget.value = String(parseFloat(input.value.replace(',', '.')))
      }
    }

    if (e?.currentTarget != null) {
      this.debouncedBroadcastInputChange(e.currentTarget as HTMLInputElement)
    }
  }
}
